import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, DataGrid, KeenIcon } from '@/components';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useSnackbar } from 'notistack';
import { ThemeRoute } from '@/types/theme-routes.types';
import {
  useGetThemeRoutesQuery,
  useDeleteThemeRouteMutation,
  useRequestThemeRouteApprovalMutation,
  usePublishThemeRouteMutation,
  useUnlockThemeRouteMutation,
} from '@/services/ThemeRoutesService';
import { usePermissions } from '@/hooks/usePermissions';
import { ConfirmationDialog } from '@/components/dialogBox';

const ThemeRoutesManagement = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();

  const canCreate = hasPermission('theme_route_management_create');
  const canUpdate = hasPermission('theme_route_management_update');
  const canDelete = hasPermission('theme_route_management_delete');
  const canApprove = hasPermission('theme_route_management_approve');
  const canRequest = hasPermission('theme_route_management_request_approval');
  const canUnlock = hasPermission('theme_route_management_revert');

  const [routes, setRoutes] = useState<ThemeRoute[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [harborSearchTerm, setHarborSearchTerm] = useState('');
  const [debouncedHarborSearchTerm, setDebouncedHarborSearchTerm] =
    useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [confirmState, setConfirmState] = useState<{
    isOpen: boolean;
    routeId: string | null;
  }>({ isOpen: false, routeId: null });

  const { data: themeRoutesResponse, isLoading } = useGetThemeRoutesQuery({
    type: 2,
  });

  useEffect(() => {
    if (themeRoutesResponse?.data) {
      setRoutes(themeRoutesResponse.data as ThemeRoute[]);
    }
  }, [themeRoutesResponse]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim().toLowerCase());
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const filteredRoutes = useMemo(() => {
    return routes.filter((route) => {
      const defaultLang = route.defaultLanguage;
      const translation =
        route.translations && defaultLang
          ? route.translations[defaultLang]
          : undefined;
      const name = route.name || translation?.name || '';
      const description = route.description || translation?.description || '';
      const themeKey = route.themeKey || '';

      const haystack = `${name} ${description} ${themeKey}`.toLowerCase();
      const baseMatches =
        !debouncedSearchTerm || haystack.includes(debouncedSearchTerm);

      const harborHaystack = (route.poiSequence || [])
        .map((poi) => poi.harborName || '')
        .join(' ')
        .toLowerCase();
      const harborMatches =
        !debouncedHarborSearchTerm ||
        harborHaystack.includes(debouncedHarborSearchTerm);

      return baseMatches && harborMatches;
    });
  }, [routes, debouncedSearchTerm, debouncedHarborSearchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedHarborSearchTerm(harborSearchTerm.trim().toLowerCase());
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [harborSearchTerm]);

  const totalItems = filteredRoutes.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const paginatedRoutes = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredRoutes.slice(startIndex, endIndex);
  }, [filteredRoutes, currentPage, pageSize]);

  const handleNavigateToCreate = () => {
    navigate('/theme-routes/create');
  };

  const handleView = useCallback(
    (routeId: string) => {
      navigate(`/theme-routes/view/${routeId}`);
    },
    [navigate]
  );

  const handleEdit = useCallback(
    (routeId: string) => {
      navigate(`/theme-routes/edit/${routeId}`);
    },
    [navigate]
  );

  const [deleteThemeRouteMutation] = useDeleteThemeRouteMutation();
  const [requestApproval, { isLoading: isRequestingApproval }] =
    useRequestThemeRouteApprovalMutation();
  const [publishRoute, { isLoading: isPublishing }] =
    usePublishThemeRouteMutation();
  const [unlockRoute, { isLoading: isUnlocking }] =
    useUnlockThemeRouteMutation();

  const handleConfirmDelete = useCallback(async () => {
    if (!confirmState.routeId) return;

    try {
      await deleteThemeRouteMutation(confirmState.routeId).unwrap();
      enqueueSnackbar('Theme route deleted', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to delete theme route', { variant: 'error' });
    } finally {
      setConfirmState({ isOpen: false, routeId: null });
    }
  }, [confirmState.routeId, deleteThemeRouteMutation, enqueueSnackbar]);

  const handleRequestApproval = useCallback(
    async (routeId: string) => {
      try {
        await requestApproval(routeId).unwrap();
        enqueueSnackbar('Approval request submitted', { variant: 'success' });
      } catch {
        enqueueSnackbar('Failed to request approval', { variant: 'error' });
      }
    },
    [enqueueSnackbar, requestApproval]
  );

  const handleUnlockRoute = useCallback(
    async (routeId: string) => {
      try {
        await unlockRoute(routeId).unwrap();
        enqueueSnackbar('Theme route unlocked for editing', {
          variant: 'success',
        });
      } catch {
        enqueueSnackbar('Failed to unlock theme route', { variant: 'error' });
      }
    },
    [unlockRoute, enqueueSnackbar]
  );

  const handlePublishRoute = useCallback(
    async (routeId: string) => {
      try {
        await publishRoute(routeId).unwrap();
        enqueueSnackbar('Theme route published', { variant: 'success' });
      } catch {
        enqueueSnackbar('Failed to publish theme route', { variant: 'error' });
      }
    },
    [publishRoute, enqueueSnackbar]
  );

  const columns = useMemo(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
        cell: ({ row }: { row: { original: ThemeRoute } }) => {
          const route = row.original;
          const translation = route.translations[route.defaultLanguage];
          const name = route.name || translation?.name || '(Untitled route)';
          return (
            <button
              type="button"
              onClick={() => handleView(route.id)}
              className="text-sm font-medium text-gray-900 hover:text-primary-active focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label={`View theme route ${name}`}
            >
              {name}
            </button>
          );
        },
        meta: { className: 'min-w-[220px]' },
      },
      {
        header: 'Theme',
        accessorKey: 'themeKey',
        cell: ({ row }: { row: { original: ThemeRoute } }) => (
          <span className="text-sm text-gray-800">
            {row.original.themeKey.replace(/_/g, ' ')}
          </span>
        ),
        meta: { className: 'min-w-[160px]' },
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ row }: { row: { original: ThemeRoute } }) => {
          const status = row.original.status;
          let label: string = status;

          switch (status) {
            case 'draft':
              label = 'Draft';
              break;
            case 'published':
              label = 'Published';
              break;
            case 'pending_approval':
              label = 'Pending approval';
              break;
            case 'approved':
              label = 'Approved';
              break;
            case 'rejected':
              label = 'Rejected';
              break;
            default:
              break;
          }

          return <span className="text-sm text-gray-800">{label}</span>;
        },
        meta: { className: 'min-w-[120px]' },
      },
      {
        header: 'POIs',
        accessorKey: 'poiCount',
        cell: ({ row }: { row: { original: ThemeRoute } }) => (
          <span className="text-sm text-gray-800">
            {(row.original.poiSequence || []).length}
          </span>
        ),
        meta: { className: 'min-w-[80px]' },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: ThemeRoute } }) => {
          const route = row.original;
          const routeLabel =
            route.name ||
            route.translations[route.defaultLanguage]?.name ||
            'route';

          const isLocked = route.isLocked === true;
          const isDraft = route.status === 'draft';
          const isPendingApproval = route.status === 'pending_approval';
          const isApproved = route.status === 'approved';
          const isPublished = route.status === 'published';

          // When locked: only View, Unlock, Publish (if approved), and Delete
          if (isLocked) {
            return (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleView(route.id)}
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  aria-label={`View ${routeLabel}`}
                >
                  <KeenIcon
                    type="filled"
                    icon="eye"
                    className="text-gray-500"
                  />
                </button>

                {canUnlock && (
                  <button
                    type="button"
                    onClick={() => handleUnlockRoute(route.id)}
                    className="btn btn-sm btn-light btn-outline flex items-center gap-1"
                    title="Unlock for editing"
                    disabled={isUnlocking}
                  >
                    <KeenIcon
                      type="filled"
                      icon="unlock"
                      className="text-amber-600"
                    />
                    <span className="text-xs">Unlock</span>
                  </button>
                )}

                {canApprove && isApproved && (
                  <button
                    type="button"
                    onClick={() => handlePublishRoute(route.id)}
                    className="btn btn-sm btn-success flex items-center gap-1"
                    disabled={isPublishing}
                  >
                    <KeenIcon
                      type="filled"
                      icon="send"
                      className="text-white"
                    />
                    <span className="text-xs">Publish</span>
                  </button>
                )}

                {canDelete && (
                  <button
                    type="button"
                    onClick={() =>
                      setConfirmState({ isOpen: true, routeId: route.id })
                    }
                    className="btn btn-sm btn-icon btn-light btn-outline"
                    aria-label={`Delete ${routeLabel}`}
                  >
                    <KeenIcon
                      type="filled"
                      icon="trash"
                      className="text-red-500"
                    />
                  </button>
                )}
              </div>
            );
          }

          // Unlocked routes: full set of actions respecting status rules
          return (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleView(route.id)}
                className="btn btn-sm btn-icon btn-light btn-outline"
                aria-label={`View ${routeLabel}`}
              >
                <KeenIcon type="filled" icon="eye" className="text-gray-500" />
              </button>

              {canUpdate && !isPendingApproval && !isPublished && (
                <button
                  type="button"
                  onClick={() => handleEdit(route.id)}
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  aria-label={`Edit ${routeLabel}`}
                >
                  <KeenIcon
                    type="filled"
                    icon="pencil"
                    className="text-gray-500"
                  />
                </button>
              )}

              {canRequest && isDraft && (
                <button
                  type="button"
                  onClick={() => handleRequestApproval(route.id)}
                  className="btn btn-sm btn-light btn-outline flex items-center gap-1"
                  disabled={isRequestingApproval}
                >
                  <KeenIcon
                    type="filled"
                    icon="check"
                    className="text-emerald-600"
                  />
                  <span className="text-xs">Request approval</span>
                </button>
              )}

              {canApprove && isApproved && (
                <button
                  type="button"
                  onClick={() => handlePublishRoute(route.id)}
                  className="btn btn-sm btn-success flex items-center gap-1"
                  disabled={isPublishing}
                >
                  <KeenIcon type="filled" icon="send" className="text-white" />
                  <span className="text-xs">Publish</span>
                </button>
              )}

              {/* Show versions except when pending approval, approved, or published */}
              {!isPendingApproval && !isApproved && !isPublished && (
                <button
                  type="button"
                  onClick={() => navigate(`/theme-routes/${route.id}/versions`)}
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  aria-label={`View versions for ${routeLabel}`}
                  title="View version history"
                >
                  <KeenIcon
                    type="filled"
                    icon="data"
                    className="text-gray-500"
                  />
                </button>
              )}

              {canDelete && (
                <button
                  type="button"
                  onClick={() =>
                    setConfirmState({ isOpen: true, routeId: route.id })
                  }
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  aria-label={`Delete ${routeLabel}`}
                >
                  <KeenIcon
                    type="filled"
                    icon="trash"
                    className="text-red-500"
                  />
                </button>
              )}
            </div>
          );
        },
        meta: { className: 'w-[140px]' },
      },
    ],
    [
      handleEdit,
      handleView,
      handleRequestApproval,
      handleUnlockRoute,
      handlePublishRoute,
      canUpdate,
      canDelete,
      canRequest,
      canApprove,
      isRequestingApproval,
      isUnlocking,
      isPublishing,
      navigate,
    ]
  );

  return (
    <>
      <Container>
        <Toolbar>
          <ToolbarHeading>
            <ToolbarPageTitle
              id="theme-routes-management-heading"
              text="Theme Routes"
            />
            <ToolbarDescription>
              Create and manage curated maritime routes by theme.
            </ToolbarDescription>
          </ToolbarHeading>
          <ToolbarActions>
            {canApprove && (
              <button
                type="button"
                className="btn btn-sm btn-light flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={() => navigate('/theme-routes/approvals')}
                aria-label="View approval requests"
              >
                <KeenIcon
                  type="filled"
                  icon="shield-check"
                  className="text-gray-500"
                />
                Approval Requests
              </button>
            )}
            {canCreate && (
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={handleNavigateToCreate}
                aria-label="Create new theme route"
              >
                New Theme Route
              </button>
            )}
          </ToolbarActions>
        </Toolbar>
      </Container>

      <Container>
        <div className="card card-grid min-w-full">
          <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 justify-between">
            <div className="flex flex-wrap items-center gap-2 sm:gap-4">
              <label
                className="input input-sm h-8 sm:h-10 flex items-center"
                htmlFor="search-theme-routes"
                aria-label="Search theme routes by name, description or theme"
              >
                <KeenIcon
                  type="filled"
                  icon="magnifier"
                  className="text-gray-500"
                />
                <input
                  id="search-theme-routes"
                  placeholder="Search theme routes"
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                  aria-label="Search theme routes by name, description or theme"
                />
              </label>
            </div>
            {(searchTerm || harborSearchTerm) && (
              <button
                type="button"
                className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={() => {
                  setSearchTerm('');
                  setDebouncedSearchTerm('');
                  setHarborSearchTerm('');
                  setDebouncedHarborSearchTerm('');
                  setCurrentPage(1);
                }}
                aria-label="Clear all filters"
              >
                <KeenIcon
                  type="solid"
                  icon="arrows-circle"
                  className="text-gray-600"
                />
                Clear Filters
              </button>
            )}
          </div>

          <div className="card-body">
            <DataGrid
              columns={columns}
              data={paginatedRoutes}
              rowSelect={false}
              loading={isLoading}
              pagination={{
                page: currentPage - 1,
                size: pageSize,
                more: currentPage < totalPages,
                totalItems,
                totalPages,
                setPagination: (updater) => {
                  const next =
                    typeof updater === 'function'
                      ? updater({
                          pageIndex: currentPage - 1,
                          pageSize,
                        })
                      : updater;
                  setCurrentPage(next.pageIndex + 1);
                  setPageSize(next.pageSize);
                },
                info: '{from} - {to} of {count}',
                sizes: [5, 10, 25, 50, 100],
                sizesLabel: 'Show',
                sizesDescription: 'per page',
              }}
              serverSide={true}
              onFilterReset={() => {
                setSearchTerm('');
                setDebouncedSearchTerm('');
                setHarborSearchTerm('');
                setDebouncedHarborSearchTerm('');
                setCurrentPage(1);
              }}
              hasActiveFilters={
                !!debouncedSearchTerm || !!debouncedHarborSearchTerm
              }
              aria-label="Theme routes table"
            />
          </div>
        </div>
      </Container>

      <ConfirmationDialog
        open={confirmState.isOpen}
        onClose={() => setConfirmState({ isOpen: false, routeId: null })}
        onConfirm={handleConfirmDelete}
        message="Are you sure you want to delete this theme route?"
        cancelButtonText="No"
        confirmButtonText="Yes"
      />
    </>
  );
};

export default ThemeRoutesManagement;
